<template>
  <tab-layout :full-width="true">
    <div slot="tabContent">
      <div class="bg-white rounded mb-4 p-3">
        <page-heading>{{ draft ? draft.name : 'Draft' }}</page-heading>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>

        <template v-else-if="!loadFailed && draft">

          <!-- Info block -->
          <div class="mb-5 pt-3 p-2 p-sm-3 bg-lighter border-top" style="font-size: 1.1rem">
            <div class="mb-2"><strong>Start Date:</strong> {{ formatDate(draft.start_date) }}</div>
            <div class="mb-2"><strong>Finish Date:</strong> {{ formatDate(draft.end_date) }}</div>
            <div class="mb-2"><strong>Status:</strong> {{ draft.state.name }}</div>
            <div><strong>Event members:</strong> {{ draft.settings.event_members_allowed ? 'Allowed' : 'Not Allowed' }}</div>
          </div>

          <!-- Draft Events -->
          <div class="d-flex justify-content-between align-items-start mb-3">
            <h2 class="h3 mb-3">Draft Events</h2>
            <router-link
              :to="{ name: 'Event Create', params: { id: draftId } }"
              class="btn btn-primary px-xl-5 mt-0"
            >Add New Event</router-link>
          </div>
          <div class="mb-5 position-relative">
            <div v-if="eventsLoading" class="table-overlay">
              <div class="table-spinner" />
            </div>
            <!-- One tbody per group: Sortable moves a whole group, so related events (a split pair,
                 a conjunction pair with its final, combined A/B halves) can never be separated. -->
            <draggable
              tag="table"
              class="table table-hover table-striped table-bordered"
              handle=".drag-handle"
              :list="eventGroups"
              :move="onMoveGroup"
              @end="onDragEnd"
            >
              <draggable
                v-for="group in eventGroups"
                :key="group.id"
                tag="tbody"
                class="event-group"
                :class="{ 'event-group--multi': group.rows.length > 1 }"
                :list="group.rows"
                :group="`unit-${group.id}`"
                handle=".child-handle"
                :move="onMoveWithinUnit"
                @end="onDragEnd"
              >
                <tr v-for="event in group.rows" :key="event.id" :data-row-id="event.id" class="event-row">
                  <td class="icon-btn icon-btn--drag drag-handle" title="Drag to move the whole group" style="vertical-align: middle; margin: 0 auto; display: table-cell" />
                  <td>
                    <row-alert
                      v-if="rowAlert.message && rowAlert.rowId === event.id"
                      above-row
                      :message="rowAlert.message"
                      :type="rowAlert.type"
                      :alert-key="rowAlert.id"
                      @dismiss="clearRowAlert"
                    />
                    <span
                      v-if="hasSiblings(event, group)"
                      class="icon-btn icon-btn--drag child-handle"
                      title="Drag to reorder within the group"
                    />
                    {{ event.name }}
                    <div class="d-md-none"><strong>Cost:</strong> ${{ event.cost }}</div>
                    <div class="d-md-none"><strong>Declared Prize Money:</strong> {{ event.declared_prize != null ? `$${event.declared_prize}` : '' }}</div>
                    <div class="d-md-none"><strong>Type:</strong> {{ event.type.name }}</div>
                    <div class="d-md-none"><strong>Final Noms:</strong> {{ event.final_noms }}</div>
                    <div class="d-md-none"><strong>Total Noms:</strong> {{ event.total_noms }}</div>
                    <div class="d-md-none"><strong>Rounds:</strong> {{ event.rounds }}</div>
                    <div class="d-md-none"><strong>Max Horses / Submissions:</strong> {{ maxHorsesOrSubmissions(event) }}</div>
                  </td>
                  <td class="d-none d-md-table-cell">${{ event.cost }}</td>
                  <td class="d-none d-md-table-cell">{{ event.declared_prize != null ? `$${event.declared_prize}` : '' }}</td>
                  <td class="d-none d-md-table-cell">{{ event.type.name }}</td>
                  <td class="d-none d-md-table-cell">{{ event.final_noms }}</td>
                  <td class="d-none d-md-table-cell">{{ event.total_noms }}</td>
                  <td class="d-none d-md-table-cell event-bg-col">{{ event.rounds }}</td>
                  <td class="d-none d-md-table-cell event-bg-col">{{ maxHorsesOrSubmissions(event) }}</td>
                  <td>
                    <div class="actions__wrap">
                      <router-link
                        :to="{ name: 'Event Edit', params: { id: draftId, eventId: event.parent_id != null ? event.parent_id : event.id } }"
                        class="icon-btn icon-btn--edit"
                        title="Edit"
                      />
                      <button
                        class="btn-actions btn-delete"
                        title="Delete"
                        :disabled="deleting"
                        @click="confirmDelete(event)"
                      />
                    </div>
                  </td>
                </tr>
                <!-- Hidden row keeps bootstrap's odd/even striping continuous across the separate tbody blocks -->
                <tr v-for="n in stripeOffset(group)" :key="`stripe-${n}`" slot="header" style="display: none" />
              </draggable>
              <!-- vuedraggable maps Sortable indexes onto the default slot's vnodes, so no whitespace text
                   node may precede the groups there. A slot's position in the template does not matter,
                   hence the header after the groups (a leading `thead` would leave a ' ' vnode in front). -->
              <thead slot="header">
                <tr>
                  <th style="width: 32px; border: none" />
                  <th>Event Name</th>
                  <th class="d-none d-md-table-cell">Cost</th>
                  <th class="d-none d-md-table-cell">Declared Prize Money</th>
                  <th class="d-none d-md-table-cell">Type</th>
                  <th class="d-none d-md-table-cell">Final Noms</th>
                  <th class="d-none d-md-table-cell">Total Noms</th>
                  <th class="d-none d-md-table-cell event-bg-col">Rounds</th>
                  <th class="d-none d-md-table-cell event-bg-col">Max Horses / Submissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody v-if="!eventGroups.length && !eventsLoading" slot="footer">
                <tr>
                  <td colspan="10" class="text-center text-muted py-3">No events found.</td>
                </tr>
              </tbody>
            </draggable>
          </div>

          <!-- Draft Rules -->
          <h2 class="h3 mb-3">Draft Rules</h2>
          <form class="mb-4" novalidate @submit.prevent="handleSaveRules">
            <!-- The result of saving appears at the top of this form -->
            <form-alert :message="rulesAlert.message.value" :type="rulesAlert.type.value" />
            <div class="form-group mb-2 d-flex align-items-center">
              <label class="committee__form-label mb-0 mr-3" style="width: 230px; flex-shrink: 0">First Round Runs Per Person</label>
              <f-text
                id="first_round_runs_per_person"
                type="number"
                :value="rules.first_round_runs_per_person"
                class="mr-2"
                style="width: 80px; flex-shrink: 0"
                @input="rules.first_round_runs_per_person = $event"
              />
              <div style="min-width: 190px; max-width: 480px; flex: 1">
                <f-search-select
                  id="exclude_first_round"
                  :value="rules.exclude_first_round"
                  :options="events"
                  :multiple="true"
                  :locked-ids="pinnedEventIds"
                  placeholder="Exclude events..."
                  @input="setExcludeFirstRound"
                />
              </div>
            </div>
            <div class="form-group mb-2 d-flex align-items-center">
              <label class="committee__form-label mb-0 mr-3" style="width: 230px; flex-shrink: 0">Total Runs Per Horse</label>
              <f-text
                id="total_runs_per_horse"
                type="number"
                :value="rules.total_runs_per_horse"
                class="mr-2"
                style="width: 80px; flex-shrink: 0"
                @input="rules.total_runs_per_horse = $event"
              />
              <div style="min-width: 190px; max-width: 480px; flex: 1">
                <f-search-select
                  id="exclude_total_runs"
                  :value="rules.exclude_total"
                  :options="events"
                  :multiple="true"
                  :locked-ids="pinnedEventIds"
                  placeholder="Exclude events..."
                  @input="setExcludeTotal"
                />
              </div>
            </div>
            <div class="mt-3">
              <button class="btn btn-primary px-xl-5" type="submit" :disabled="submittingRules">Save Rules</button>
            </div>
          </form>

          <!-- Filter Nominations -->
          <draft-filter-nominations :events="events" />

        </template>

        <confirm-modal
          v-if="eventToDelete"
          :message="`You are about to delete event ${eventToDelete.name}. Are you sure?`"
          :loading="deleting"
          @confirm="handleDelete"
          @cancel="eventToDelete = null"
        />
      </div>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, computed, onMounted, ref, watch } from '@vue/composition-api'
import Draggable from 'vuedraggable'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import ConfirmModal from '@/components/ConfirmModal'
import FormAlert from '@/components/FormAlert'
import FText from '@/components/FormFields/FText'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import useRowAlert from '@/composables/useRowAlert'
import RowAlert from '@/components/RowAlert'
import EventBus from '@/store/EventBus'
import useDraftView from '@/composables/drafts/useDraftView'
import useDraftEvents from '@/composables/drafts/useDraftEvents'
import useDraftRules from '@/composables/drafts/useDraftRules'
import { formatDate } from '@/utils/date'
import { buildLeafEventGroups } from '@/utils/eventGroups'
import DraftFilterNominations from './components/DraftFilterNominations'

export default defineComponent({
  name: 'SecretaryDraftView',
  components: { Draggable, RowAlert, TabLayout, PageHeading, ConfirmModal, FormAlert, FText, FSearchSelect, DraftFilterNominations },
  props: {
    draftId: { type: [String, Number], required: true }
  },
  setup(props) {
    const { draft, loading, loadFailed, loadDraft } = useDraftView()
    // Results of reorder / delete appear right above the event they were done on (above-row alert in
    // its row); anything not tied to an event goes to the page-top alert as before.
    const { alert: rowAlert, show: showRowAlert, clear: clearRowAlert } = useRowAlert()
    let alertRowId = null
    const { events, loading: eventsLoading, deleting, loadEvents, deleteEvent, reorderEvents } = useDraftEvents({
      onError: msg => (alertRowId != null ? showRowAlert(msg, 'warning', alertRowId) : EventBus.$emit('http-error', msg)),
      onSuccess: msg => (alertRowId != null ? showRowAlert(msg, 'success', alertRowId) : EventBus.$emit('show-success', msg))
    })
    const { rules, submitting: submittingRules, alert: rulesAlert, pinnedEventIds, initRules, saveRules, setExcludeFirstRound, setExcludeTotal } = useDraftRules()

    const eventToDelete = ref(null)

    function confirmDelete(event) {
      eventToDelete.value = event
    }

    async function handleDelete() {
      if (!eventToDelete.value) return
      const id = eventToDelete.value.id
      eventToDelete.value = null
      // The row is gone once the result is shown: anchor on the event that follows it (or precedes it)
      const list = events.value
      const index = list.findIndex(e => e.id === id)
      const neighbour = index === -1 ? null : (list[index + 1] || list[index - 1])
      alertRowId = neighbour != null ? neighbour.id : null
      await deleteEvent(id)
    }

    // Local mutable copy: Sortable splices the list it is given in place.
    const eventGroups = ref([])
    watch(events, (list) => { eventGroups.value = buildLeafEventGroups(list) }, { immediate: true })

    // Row position at which each group starts, to keep striping continuous across tbody blocks.
    const groupStart = computed(() => {
      const starts = {}
      let n = 0
      eventGroups.value.forEach((group) => {
        starts[group.id] = n
        n += group.rows.length
      })
      return starts
    })

    // 1 when the group starts on an odd row — rendered as a hidden header row (see the template).
    // A `v-for`, not a `v-if`: a false `v-if` leaves an empty comment vnode in vuedraggable's default
    // slot, which shifts every row index it computes by one (drag-and-drop then moves the wrong row
    // and can splice `undefined` into the list).
    function stripeOffset(group) {
      return groupStart.value[group.id] % 2
    }

    // Groups may be dropped between each other but never before the header row. (No `draggable`
    // selector on the table: vuedraggable assumes Sortable's indexes include the header slot.)
    function onMoveGroup(evt) {
      return evt.related.tagName === 'TBODY'
    }

    // Children of one parent (E1/E2, A/B halves) may swap places, but never leave their own unit.
    function hasSiblings(event, group) {
      return event.parent_id != null && group.rows.filter(row => row.parent_id === event.parent_id).length > 1
    }

    function onMoveWithinUnit(evt) {
      const related = evt.relatedContext.element
      return related != null && related.parent_id === evt.draggedContext.element.parent_id
    }

    function onDragEnd(evt) {
      // The dragged row (a group's first row, or the child row) — set via data-row-id in the template
      const item = evt && evt.item
      const holder = item && (item.dataset.rowId != null ? item : item.querySelector('[data-row-id]'))
      const movedId = holder ? Number(holder.dataset.rowId) : null
      alertRowId = Number.isNaN(movedId) ? null : movedId
      const ordered = eventGroups.value.flatMap(group => group.rows)
      events.value = ordered
      reorderEvents(ordered.map(e => e.id))
    }

    function maxHorsesOrSubmissions(event) {
      if (event.requires_horse === false) {
        return event.max_submissions != null ? event.max_submissions : ''
      }
      return event.max_horses === 0 ? '' : event.max_horses
    }

    async function handleSaveRules() {
      await saveRules(props.draftId)
    }

    onMounted(async () => {
      await Promise.all([
        loadDraft(props.draftId),
        loadEvents(props.draftId)
      ])
      if (draft.value) {
        initRules(draft.value, events.value)
      }
    })

    return {
      draft,
      loading,
      loadFailed,
      events,
      eventGroups,
      groupStart,
      rowAlert,
      clearRowAlert,
      stripeOffset,
      onMoveGroup,
      hasSiblings,
      onMoveWithinUnit,
      eventsLoading,
      deleting,
      eventToDelete,
      confirmDelete,
      handleDelete,
      onDragEnd,
      maxHorsesOrSubmissions,
      pinnedEventIds,
      setExcludeFirstRound,
      setExcludeTotal,
      rules,
      submittingRules,
      rulesAlert,
      handleSaveRules,
      formatDate
    }
  }
})
</script>

<style scoped>
/* Hosts the row's own alert, positioned against it (see RowAlert's above-row) */
.event-row {
  position: relative;
}

.table th:last-child,
.table td:last-child {
  width: 1px;
  white-space: nowrap;
}

.event-bg-col {
  background: #ffeeba;
}

/* The handle cell has no bootstrap borders of its own, so keep the row dividers through it */
.event-group td.drag-handle {
  border: none;
  border-top: 1px solid #dee2e6;
}

.child-handle {
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
  opacity: 0.45;
  transform: scale(0.8);
  cursor: grab;
}

.event-group--multi {
  border-top: 2px solid var(--gray-400);
  border-bottom: 2px solid var(--gray-400);
}
</style>



// WEBPACK FOOTER //
// src/views/Drafts/View/SecretaryDraftView.vue